import {
  ENDPOINTS,
  SearchBoostListRequestSchema,
  SearchJudgementListRequestSchema,
  SearchStabilityRequestSchema,
  type ManageClauseResponse,
  type SearchBoostListRequest,
  type SearchBoostListResponse,
  type SearchJudgement,
  type SearchJudgementListRequest,
  type SearchJudgementListResponse,
  type SearchStabilityRequest,
  type SearchStabilityResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';
import { manageClause } from './clauses';

/**
 * `search-judgements`, `search-boosts`, `search-stability` and the three
 * feedback writes on `manage-clause` — the search feedback loops, wrapped for
 * the browser (CLM-39). `clauses.ts` is the precedent: the path comes from the
 * registry, the request is `.parse`d before it leaves the browser, and the
 * response is **parsed** rather than asserted, so an off-contract answer is a
 * `ZodError` here and never data on screen.
 *
 * The two lists are GETs, so the request object is flattened the one way
 * `apps/api/src/http/decode.ts` reads: dotted keys for the nested page object
 * (`page.limit`, `page.cursor`) and booleans as the literal `true`/`false`.
 * Stability is a POST for the same reason `search` is — a query string would
 * put search terms in access logs.
 *
 * The writes are thin wrappers over `manageClause` with the action literal, so
 * a surface names what it does (`recordJudgement`) rather than the union member.
 */

const JUDGEMENTS_ENDPOINT = ENDPOINTS['search-judgements'];
const BOOSTS_ENDPOINT = ENDPOINTS['search-boosts'];
const STABILITY_ENDPOINT = ENDPOINTS['search-stability'];

/** Flatten a validated judgements request into the query the decoder expands. */
export function judgementListQuery(request: SearchJudgementListRequest): Query {
  const query: Record<string, string | number | boolean> = {};
  if (request.stale_only !== undefined) query['stale_only'] = request.stale_only;
  if (request.clause_code !== undefined) query['clause_code'] = request.clause_code;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export function boostListQuery(request: SearchBoostListRequest): Query {
  const query: Record<string, string | number | boolean> = {};
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export async function fetchJudgements(
  request: SearchJudgementListRequest,
  signal?: AbortSignal,
): Promise<SearchJudgementListResponse> {
  const validated = SearchJudgementListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(JUDGEMENTS_ENDPOINT.path, {
    query: judgementListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return JUDGEMENTS_ENDPOINT.response.parse(raw);
}

export async function fetchBoosts(request: SearchBoostListRequest, signal?: AbortSignal): Promise<SearchBoostListResponse> {
  const validated = SearchBoostListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(BOOSTS_ENDPOINT.path, {
    query: boostListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return BOOSTS_ENDPOINT.response.parse(raw);
}

export async function fetchStability(request: SearchStabilityRequest, signal?: AbortSignal): Promise<SearchStabilityResponse> {
  const body = SearchStabilityRequestSchema.parse(request);
  const raw = await apiPost<unknown>(STABILITY_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return STABILITY_ENDPOINT.response.parse(raw);
}

/* -------------------------------------------------------------------------- */
/* The writes — manage-clause actions                                          */
/* -------------------------------------------------------------------------- */

/** `record_search_judgement`. `search_id` is always sent from a result row, so the API copies the logged vector and embeds nothing. */
export function recordJudgement(
  input: { readonly query_text: string; readonly clause_code: string; readonly judgement: SearchJudgement; readonly search_id: string },
  signal?: AbortSignal,
): Promise<ManageClauseResponse> {
  return manageClause({ action: 'record_search_judgement', ...input }, signal);
}

export function retractJudgement(feedback_id: string, signal?: AbortSignal): Promise<ManageClauseResponse> {
  return manageClause({ action: 'retract_search_judgement', feedback_id }, signal);
}

export function setClauseBoost(
  input: { readonly clause_code: string; readonly boost_weight: number; readonly reason: string },
  signal?: AbortSignal,
): Promise<ManageClauseResponse> {
  return manageClause({ action: 'set_clause_boost', ...input }, signal);
}
